/**
 * call-waiting — is there a SECOND call competing for the agent's attention?
 *
 * THE DEFECT THIS FIXES
 * ---------------------
 * The call-waiting banner is an "Incoming call waiting" strip with Accept and
 * Reject buttons, shown above the in-call controls. Its condition used to be:
 *
 *     incomingCall && activeCall && activeCall.status is answered|on_hold
 *
 * which never checked that the two were DIFFERENT calls — and for a normal
 * inbound call they are the same one:
 *
 *   1. `call:incoming` arrives over the websocket -> `setIncoming()` caches it
 *      as `incomingCall`.
 *   2. The SIP INVITE reaches this agent -> `activateFromIncoming()` copies
 *      that cache into `activeCall` (status `ringing`) but deliberately LEAVES
 *      `incomingCall` set, because `setAnswered()` still reads it as a
 *      fallback when `activeCall` is absent.
 *   3. The agent answers. `activeCall.status` becomes `answered` while
 *      `incomingCall` still refers to that very same call.
 *
 * All three clauses were then true, so the call the agent was already talking
 * on advertised ITSELF as a second call waiting. Observed live: a connected
 * call showing "In Call 0:03" with an "Incoming call waiting / Accept /
 * Reject" prompt for the same caller.
 *
 * That is not cosmetic. Accept runs the swap path — hold the current call and
 * answer the "other" one — against a call that does not exist, and Reject
 * invites the agent to hang up on the person they are speaking to.
 *
 * Comparing call IDs is the whole fix: a call is only WAITING if it is not the
 * call already in progress. A pure function so the cases below are testable
 * without mounting the layout or driving a websocket.
 */

export interface CallWaitingInput {
  /** `callId` of the cached `call:incoming` event, if any. */
  incomingCallId?: string | null | undefined
  /** `callId` of the call currently on this softphone, if any. */
  activeCallId?: string | null | undefined
  /** `activeCall.status` — only an established call can have another waiting. */
  activeStatus?: string | null | undefined
}

/**
 * True only when a genuinely different call is ringing while the agent is on
 * an established call.
 *
 * `answered` and `on_hold` are the only statuses that qualify. A `ringing`
 * active call is the agent's own unanswered call — it renders the full-screen
 * answer/reject UI, and showing the waiting strip underneath would offer two
 * competing ways to accept the same call.
 */
export function isCallWaiting(input: CallWaitingInput): boolean {
  const { incomingCallId, activeCallId, activeStatus } = input

  // No second call, or nothing established to interrupt.
  if (incomingCallId == null || incomingCallId === '') return false
  if (activeCallId == null || activeCallId === '') return false

  // The one that bit: same call seen through two store fields.
  if (incomingCallId === activeCallId) return false

  return activeStatus === 'answered' || activeStatus === 'on_hold'
}
